import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  GET_TOP_COMMON_ISSUES,
  type TopCommonIssueRow,
  type TopCommonIssuesResponse,
  type TopCommonIssuesVariables,
} from "./TopCommonIssuesApi";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import TableChart, { type TableChartRowData } from "../TableChart";
import useSetDataWithExport from "../useSetDataWithExport";
import "./TopCommonIssues.scss";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../../../General/useSeverityAsPriority";
import useWidgetFilterNavigator from "../../../useWidgetFilterNavigator";
import {
  getFormattedDate,
  Tooltip,
  useIcon,
  TableCell,
  tableCellTypesEnum,
  normalizeNumbers,
} from "@SeemplicityDev/shared";
import type { SelectedScope } from "../../../../types/scope";

interface TopCommonIssuesProps {
  selectedScope?: SelectedScope | null;
  scopeToExecute?: string | null;
  filterToExecute?: string | null;
  isForExport?: boolean;
  setTotalWidgets?: ((total: number) => void) | null;
  index?: number | null;
  setIsReadyToExport?: ((ready: boolean) => void) | null;
}

const TopCommonIssues: React.FC<TopCommonIssuesProps> = ({
  selectedScope,
  scopeToExecute,
  filterToExecute,
  isForExport = false,
  setTotalWidgets = null,
  index = null,
  setIsReadyToExport = null,
}) => {
  const [tableData, setTableData] = useState<TopCommonIssueRow[]>([]);
  const { getSeverityField } = useSeverityAsPriority();
  const { getDataSourceIcon } = useIcon(isForExport ? "LIGHT" : undefined);

  const {
    data: topCommonIssuesData,
    loading: topCommonIssuesLoading,
    error: topCommonIssuesError,
    refetch,
  } = useQuery<TopCommonIssuesResponse, TopCommonIssuesVariables>(
    GET_TOP_COMMON_ISSUES,
    {
      variables: {
        filters_config: {
          filtersjson: filterToExecute || null,
          scopesjson: scopeToExecute || null,
          scopesid: selectedScope?.value?.id,
        },
      },
      skip: !selectedScope?.value?.id && !scopeToExecute,
    }
  );

  const { setDataWithExport, setLoadedRowsCount } = useSetDataWithExport({
    isForExport,
    index,
    setTotalWidgets,
    setData: setTableData,
    data: tableData,
    error: topCommonIssuesError,
    loading: topCommonIssuesLoading,
    setIsReadyToExport,
  });

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: scopeToExecute,
    scopeId: selectedScope?.value?.id,
  });

  const columnClick = (
    rule_id: string,
    source: string,
    category: string,
    severity: string
  ) => {
    onFilterClick([
      ["in", "remediation_item.rule_id", rule_id],
      ["in", "remediation_item.source", source],
      ["in", "remediation_item.category", category],
      ["in", "remediation_item.severity", severity],
    ]);
  };

  useEffect(() => {
    if (!!topCommonIssuesData?.top_common_issues?.common_issues) {
      let parsedData: TopCommonIssueRow[] | undefined;

      try {
        parsedData = JSON.parse(
          topCommonIssuesData.top_common_issues?.common_issues
        );
      } catch (e) {
        console.error("Failed to parse top common issues data", e);
      }

      if (!!parsedData) {
        setDataWithExport(parsedData);
        setLoadedRowsCount(parsedData?.length);
      }
    }
  }, [topCommonIssuesData]);

  const columnHelper = createColumnHelper<TopCommonIssueRow>();

  const columns = useMemo<ColumnDef<TableChartRowData, unknown>[]>(() => {
    return [
      columnHelper.accessor("name", {
        cell: (props) => (
          <TableCell>
            <Tooltip content={props.getValue()}>
              <span>{props.getValue()}</span>
            </Tooltip>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Title",
        maxSize: 350,
        minSize: 300,
      }),
      columnHelper.accessor("source", {
        cell: (props) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <Tooltip type="info" content={`${props.getValue()}`}>
              <img
                alt={props.getValue()}
                src={
                  getDataSourceIcon(props.getValue(), "S") as string | undefined
                }
                className={"data-source-icon"}
              />
            </Tooltip>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Source",
        minSize: 50,
        maxSize: 60,
      }),
      columnHelper.accessor("category", {
        cell: (props) => (
          <TableCell>
            <Tooltip content={props.getValue()}>
              <span>{props.getValue()}</span>
            </Tooltip>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Category",
        minSize: 90,
        maxSize: 100,
      }),
      columnHelper.accessor("count", {
        cell: (props) => {
          const { rule_id, datasource_name, category, severity } =
            props.row.original;
          return (
            <TableCell type={tableCellTypesEnum.COMPONENT}>
              <Tooltip
                type="info"
                content={`${normalizeNumbers(props.getValue())} ${getSeverityField(severity, SeverityFieldsEnum.NAME)} Findings`}
              >
                <div
                  className={"findings-count-wrapper"}
                  onClick={() =>
                    columnClick(rule_id, datasource_name, category, severity)
                  }
                >
                  <div
                    className={"severity-dot"}
                    style={{
                      backgroundColor: getSeverityField(
                        severity,
                        SeverityFieldsEnum.COLOR
                      ),
                    }}
                  />
                  <span>{normalizeNumbers(props.getValue())}</span>
                </div>
              </Tooltip>
            </TableCell>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
        header: () => (
          <div className={"findings-count-header"}>
            No. Occurrences
            <Tooltip
              type="info"
              content="There can be more than one descriptive title associated with the total occurrences"
            >
              <i className={"seem-icon-help"} />
            </Tooltip>
          </div>
        ),
        minSize: 90,
        maxSize: 120,
      }),
    ] as ColumnDef<TableChartRowData, unknown>[];
  }, [tableData, getDataSourceIcon]);

  return (
    <div className={"top-common-issues"}>
      <TableChart
        className={"top-common-issues-table"}
        columns={columns}
        tableData={tableData as unknown as TableChartRowData[]}
        isLoading={topCommonIssuesLoading}
        error={topCommonIssuesError}
        onRefetch={refetch}
        isForExport={isForExport}
      />
      {!!topCommonIssuesData?.top_common_issues?.last_updated && (
        <div className={"last-updated"}>
          <Tooltip
            type="info"
            content="This widget refreshes daily and displays according to your selected scope (not including filters"
          >
            <div className="last-updated-text">
              Last Update:{" "}
              <span className={"semibold"}>
                {getFormattedDate(
                  topCommonIssuesData?.top_common_issues?.last_updated
                )}
              </span>
            </div>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default TopCommonIssues;
